import { type Dispatch, type SetStateAction, useState, useMemo, useRef, forwardRef, useImperativeHandle } from "react";
import { useDroppable, type DragEndEvent } from "@dnd-kit/core";
import type { EventPublic, RugbyEventMatchResultSummary, MatchPublic, MatchTeamPublic } from "@dugads/shared";
import { defaultMatchTimes, conflictingMatchIds } from "@dugads/shared";
import * as api from "@/lib/api";
import {
  COURT_COLORS,
  type DraggableTeam,
  generateTimeSlots,
} from "@/lib/scheduler-utils";
import { MatchCard, matchNeedsOpponent } from "@/components/event-detail/match-card";
import { ShiftMatchesDialog, type ShiftMode } from "@/components/event-detail/shift-matches-dialog";
import { notifySaving, notifySaved } from "@/lib/save-status";
import { directionsUrl } from "@/lib/directions";

/** 184: imperative handle exposed to the scheduler's DndContext (in
 *  event-detail, which spans the team panel + this grid). */
export type ScheduleGridHandle = { onDragEnd: (e: DragEndEvent) => void };

// 184: one grid cell as a dnd-kit drop target. Each cell carries its court+time
// so the central onDragEnd needs no pointer math: dropping a team on an empty
// cell creates a match there; on an occupied cell (resolved via findMatchAt) it
// sets the opponent; dropping a match card reschedules it to that cell.
function CellDroppable({ courtId, time, slotHeight, className }: { courtId: string; time: string; slotHeight: number; className: string }) {
  const { setNodeRef, isOver } = useDroppable({ id: `cell-${courtId}-${time}`, data: { kind: "cell", courtId, time } });
  return (
    <div
      ref={setNodeRef}
      className={`${className} ${isOver ? "bg-primary/10 ring-1 ring-inset ring-primary/30" : ""}`}
      style={{ height: slotHeight }}
    />
  );
}

const SLOT_MINS = 5;
const SLOT_HEIGHT = 10;
const PX_PER_MIN = SLOT_HEIGHT / SLOT_MINS;
const MAJOR_LABEL_EVERY_MINS = 30;

/** For ad-hoc teams (no teamId), prefix with school name to avoid cross-school name collisions */
function teamMatchLabel(team: DraggableTeam): string {
  if (team.teamId) return team.name;
  return `${team.schoolName} ${team.name}`;
}

interface ScheduleGridProps {
  event: EventPublic;
  matches: MatchPublic[];
  matchTeams: Record<string, MatchTeamPublic[]>;
  matchResults?: Record<string, RugbyEventMatchResultSummary>;
  matchFlashes?: Record<string, { type: string; side: string; points: number; at: number }>;
  teamColorMap: Map<string, number>;
  teamSchoolMap: Map<string, string>;
  canManageMatches: boolean;
  /** 193 -- the per-sport court noun ("Field"/"Court"/"Pitch") for the shift
   *  dialog wording. */
  courtLabel?: string;
  setMatches: Dispatch<SetStateAction<MatchPublic[]>>;
  setMatchTeams: Dispatch<SetStateAction<Record<string, MatchTeamPublic[]>>>;
  refreshMatches: () => Promise<void>;
  /** 129 # 128 -- full event refetch (fire-and-forget), used after
   *  counterparty.team_names is mutated so the team-panel column
   *  repopulates. */
  reloadEvent?: () => void;
}

export const ScheduleGrid = forwardRef<ScheduleGridHandle, ScheduleGridProps>(function ScheduleGrid({ event, matches, matchTeams, matchResults, matchFlashes, teamColorMap, teamSchoolMap, canManageMatches, courtLabel = "Court", setMatches, setMatchTeams, refreshMatches, reloadEvent }, ref) {
  // ─── Derived state ────────────────────────────────────────────

  const sortedCourts = useMemo(() =>
    [...event.courts].sort((a, b) => {
      const aVid = a.event_venue_id;
      const bVid = b.event_venue_id;
      if (aVid !== bVid) {
        if (!aVid) return 1;
        if (!bVid) return -1;
        const aName = event.venues.find((v) => v.id === aVid)?.display_name ?? "";
        const bName = event.venues.find((v) => v.id === bVid)?.display_name ?? "";
        return aName.localeCompare(bName);
      }
      return a.name.localeCompare(b.name);
    }),
  [event.courts, event.venues]);

  const displayCourts = useMemo(() => {
    if (sortedCourts.length > 0) return sortedCourts.map((c) => ({ id: c.id, name: c.name, is_adhoc: c.is_adhoc, event_venue_id: c.event_venue_id }));
    return [{ id: "__none__", name: "Matches", is_adhoc: false, event_venue_id: null as string | null }];
  }, [sortedCourts]);

  const timeSlots = useMemo(() =>
    generateTimeSlots(event.starts_at, event.ends_at, SLOT_MINS),
  [event.starts_at, event.ends_at]);

  const gridStartMs = useMemo(() => {
    const d = new Date(event.starts_at);
    d.setMinutes(Math.floor(d.getMinutes() / SLOT_MINS) * SLOT_MINS, 0, 0);
    return d.getTime();
  }, [event.starts_at]);

  const matchesByCourt = useMemo(() => {
    const map = new Map<string, MatchPublic[]>();
    for (const m of matches) {
      const cid = m.event_court_id ?? "__none__";
      const list = map.get(cid) ?? [];
      list.push(m);
      map.set(cid, list);
    }
    return map;
  }, [matches]);

  // 191 -- same-field overlaps, recomputed on every create/move/resize/delete
  // (matches state changes). Informational red framing only; drags proceed.
  const conflictIds = useMemo(() => conflictingMatchIds(matches), [matches]);

  // 193 -- multi-select + bulk shift. Selection moves are TIME-ONLY (locked
  // at activation): dragging any selected card shifts the whole selection by
  // the time delta; nobody changes court.
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [shiftMode, setShiftMode] = useState<ShiftMode | null>(null);

  function toggleSelected(matchId: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(matchId)) next.delete(matchId); else next.add(matchId);
      return next;
    });
  }

  function exitSelectMode() {
    setSelectMode(false);
    setSelectedIds(new Set());
  }

  /** 193 -- optimistic bulk time shift (drag path; the dialog handles its own). */
  async function bulkShiftByIds(ids: string[], deltaMins: number) {
    const deltaMs = deltaMins * 60_000;
    const idSet = new Set(ids);
    setMatches((prev) => prev.map((m) => idSet.has(m.id)
      ? {
        ...m,
        starts_at: new Date(new Date(m.starts_at).getTime() + deltaMs).toISOString(),
        ends_at: new Date(new Date(m.ends_at).getTime() + deltaMs).toISOString(),
      }
      : m));
    notifySaving();
    try {
      await api.shiftMatches(event.id, { delta_mins: deltaMins, match_ids: ids });
      notifySaved();
    } catch { refreshMatches(); }
  }

  function findMatchAt(courtId: string, timeHHMM: string, excludeId?: string | null): MatchPublic | undefined {
    const courtList = matchesByCourt.get(courtId);
    if (!courtList) return undefined;
    const [h, m] = timeHHMM.split(":").map(Number);
    const slotMin = h * 60 + m;
    for (const match of courtList) {
      if (excludeId && match.id === excludeId) continue;
      const mStart = new Date(match.starts_at);
      const mStartMin = mStart.getHours() * 60 + mStart.getMinutes();
      const mEndMin = mStartMin + match.first_half_mins + match.break_mins + match.second_half_mins;
      if (slotMin >= mStartMin && slotMin < mEndMin) return match;
    }
    return undefined;
  }

  // ─── Optimistic helpers ───────────────────────────────────────

  function optimisticUpdateMatch(matchId: string, patch: Partial<MatchPublic>) {
    setMatches((prev) => prev.map((m) => m.id === matchId ? { ...m, ...patch } : m));
  }

  function optimisticSetMatchTeam(matchId: string, role: string, teamId: string, teamName: string) {
    setMatchTeams((prev) => {
      const existing = (prev[matchId] ?? []).filter((t) => t.role !== role);
      return { ...prev, [matchId]: [...existing, { match_id: matchId, team_id: teamId, role: role as MatchTeamPublic["role"], team_name: teamName, age_group: "", school_name: null, school_abbreviation: null, school_logo_url: null, school_primary_color: null, school_secondary_color: null }] };
    });
  }

  function optimisticRemoveMatchTeam(matchId: string, role: string) {
    setMatchTeams((prev) => ({
      ...prev,
      [matchId]: (prev[matchId] ?? []).filter((t) => t.role !== role),
    }));
  }

  // ─── Drag-and-Drop ────────────────────────────────────────────

  const hostSchoolName = event.schools.find((s) => s.is_host)?.name;

  // 129 # 128 -- single counterparty school (typically the non-Dugads
  // opponent). When exactly one exists, MatchCard renders a "+ 1-2-1"
  // button on the TBD away pill so users can mirror the host team onto
  // the counterparty without leaving the event.
  const nonHostSchools = event.schools.filter((s) => !s.is_host);
  const singleCounterparty = nonHostSchools.length === 1 ? nonHostSchools[0] : null;

  /** Mirror the host team's name onto the counterparty school's team_names
   *  and assign it to this match's away slot in one click. Builds on the
   *  existing external-team path (event_schools.team_names + teamMatchLabel
   *  prefixing in handleTeamDropOnMatch). */
  async function handleQuickPairCounterparty(targetMatch: MatchPublic) {
    if (!singleCounterparty) return;
    const [leftName, rightName] = targetMatch.name.split(" vs ");
    if (leftName === "TBD" || rightName !== "TBD") return;

    const existingNames = singleCounterparty.team_names ?? [];
    if (!existingNames.includes(leftName)) {
      try {
        await api.updateEventSchool(event.id, singleCounterparty.id, {
          team_names: [...existingNames, leftName],
        });
        // 129 # 128 -- refetch the event so the team-panel column for the
        // counterparty picks up the new external team. refreshMatches alone
        // only touches matches + match_teams, not event_schools. Fire-and-
        // forget: setEvent will trigger the schoolTeams useEffect.
        reloadEvent?.();
      } catch {
        refreshMatches();
        return;
      }
    }

    const team: DraggableTeam = {
      id: `ext-${singleCounterparty.name}-${leftName}-${Date.now()}`,
      name: leftName,
      schoolName: singleCounterparty.name,
      teamId: null,
      ageGroupId: null,
      ageGroupName: null,
      defaultFirstHalfMins: null,
      defaultBreakMins: null,
      defaultSecondHalfMins: null,
      colorIndex: 1,
    };
    await handleTeamDropOnMatch(targetMatch, team);
    await refreshMatches();
  }

  /** Called from MatchCard when a team is dropped onto an existing match. */
  async function handleTeamDropOnMatch(existingMatch: MatchPublic, team: DraggableTeam) {
    const [left, right] = existingMatch.name.split(" vs ");
    const mt = matchTeams[existingMatch.id] ?? [];
    const isHostTeam = team.schoolName === hostSchoolName;

    if (matchNeedsOpponent(existingMatch)) {
      const homeIsTBD = left === "TBD";
      const label = teamMatchLabel(team);
      const matchName = homeIsTBD ? `${label} vs ${right}` : `${left} vs ${label}`;
      const role = homeIsTBD ? "home" : "away";
      optimisticUpdateMatch(existingMatch.id, { name: matchName });
      if (team.teamId) optimisticSetMatchTeam(existingMatch.id, role, team.teamId, team.name);
      try {
        await api.updateMatch(existingMatch.id, { name: matchName });
        if (team.teamId) await api.addMatchTeam(existingMatch.id, { team_id: team.teamId, role });
      } catch { refreshMatches(); }
      return;
    }

    // Replace existing side
    const role = isHostTeam ? "home" : "away";
    const existingTeam = mt.find((t) => t.role === role);
    if (existingTeam?.team_id === team.teamId && team.teamId) return;
    const label = teamMatchLabel(team);
    const matchName = role === "home" ? `${label} vs ${right}` : `${left} vs ${label}`;
    optimisticUpdateMatch(existingMatch.id, { name: matchName });
    if (existingTeam) optimisticRemoveMatchTeam(existingMatch.id, role);
    if (team.teamId) optimisticSetMatchTeam(existingMatch.id, role, team.teamId, team.name);
    try {
      if (existingTeam) await api.removeMatchTeam(existingMatch.id, existingTeam.team_id);
      await api.updateMatch(existingMatch.id, { name: matchName });
      if (team.teamId) await api.addMatchTeam(existingMatch.id, { team_id: team.teamId, role });
    } catch { refreshMatches(); }
  }

  /** 184: create a new match from a team dropped on an empty grid cell. */
  async function createMatchFromTeam(courtId: string, time: string, team: DraggableTeam) {
    const isHostTeam = team.schoolName === hostSchoolName;
    const role = isHostTeam ? "home" : "away";
    const label = teamMatchLabel(team);
    const matchName = isHostTeam ? `${label} vs TBD` : `TBD vs ${label}`;
    const dbDefaults = team.defaultFirstHalfMins != null ? { first_half_mins: team.defaultFirstHalfMins, break_mins: team.defaultBreakMins!, second_half_mins: team.defaultSecondHalfMins! } : null;
    const times = defaultMatchTimes(team.ageGroupName, dbDefaults);
    const [h, m] = time.split(":").map(Number);
    const matchStart = new Date(event.starts_at);
    matchStart.setHours(h, m, 0, 0);

    const courtEntry = displayCourts.find((c) => c.id === courtId);
    let createdMatchId: string | null = null;
    let createdMatch: MatchPublic | null = null;
    try {
      const res = await api.createMatch(event.id, {
        name: matchName,
        starts_at: matchStart.toISOString(),
        event_court_id: courtId === "__none__" ? undefined : courtId,
        event_venue_id: courtEntry?.event_venue_id ?? undefined,
        sport_id: event.sport_id ?? undefined,
        first_half_mins: times.first_half_mins,
        break_mins: times.break_mins,
        second_half_mins: times.second_half_mins,
      });
      createdMatchId = res.match.id;
      createdMatch = res.match;
      setMatches((prev) => [...prev, res.match]);
      if (team.teamId) {
        await api.addMatchTeam(res.match.id, { team_id: team.teamId, role });
        optimisticSetMatchTeam(res.match.id, role, team.teamId, team.name);
      }
    } catch {
      // 127 R138 -- if createMatch succeeded but addMatchTeam failed, the
      // match would otherwise be left orphaned (no MatchTeam, just a TBD-vs-X
      // name). Delete the orphan so the user doesn't see a half-built match
      // they didn't intend to create.
      if (createdMatchId) {
        try { await api.deleteMatch(createdMatchId); } catch { /* ignore */ }
      }
      refreshMatches();
      return;
    }

    // 129 # 128 -- auto-pair counterparty when a host team is dragged to
    // create a new match AND exactly one non-host EventSchool exists.
    // Mirrors what the "+ 1-2-1" button does on an already-created TBD
    // match, so the common single-opponent case is one drag, not drag +
    // click. Wrapped in its own try so a counterparty failure leaves the
    // valid "host vs TBD" match intact instead of triggering the orphan
    // cleanup above.
    if (createdMatch && isHostTeam && singleCounterparty) {
      try {
        await handleQuickPairCounterparty(createdMatch);
      } catch { /* leave the host-vs-TBD match in place */ }
    }
  }

  /** 184: reschedule a match dragged onto a different cell (court + snapped
   *  time). Keeps the match's date; only the time-of-day and court change. */
  async function moveMatch(match: MatchPublic, courtId: string, time: string) {
    const [h, mm] = time.split(":").map(Number);
    const newStart = new Date(match.starts_at);
    newStart.setHours(h, mm, 0, 0);
    const totalMins = match.first_half_mins + match.break_mins + match.second_half_mins;
    const newEnd = new Date(newStart.getTime() + totalMins * 60_000);
    const newEventCourtId = courtId === "__none__" ? null : courtId;
    const startIso = newStart.toISOString();
    if (match.starts_at === startIso && match.event_court_id === newEventCourtId) return;
    optimisticUpdateMatch(match.id, { starts_at: startIso, ends_at: newEnd.toISOString(), event_court_id: newEventCourtId });
    try {
      await api.updateMatch(match.id, { starts_at: startIso, event_court_id: newEventCourtId });
    } catch { refreshMatches(); }
  }

  // 184: central drop router for the scheduler's DndContext (which lives in
  // event-detail and spans the team panel + this grid). The cell droppable
  // carries its court+time, so no pointer geometry is needed:
  //   - team  -> empty cell : create a match there
  //   - team  -> occupied cell : set/replace that match's opponent
  //   - match -> empty cell : reschedule to that court + time
  function handleDragEnd(e: DragEndEvent) {
    if (!canManageMatches) return;
    const active = e.active.data.current;
    const over = e.over?.data.current;
    if (!active || !over || over.kind !== "cell") return;
    const courtId = over.courtId as string;
    const time = over.time as string;

    if (active.kind === "team") {
      const team = active.team as DraggableTeam;
      const existing = findMatchAt(courtId, time);
      if (existing) {
        handleTeamDropOnMatch(existing, team);
      } else {
        createMatchFromTeam(courtId, time, team);
      }
      return;
    }

    if (active.kind === "match-move") {
      const matchId = active.matchId as string;
      const match = matches.find((mm) => mm.id === matchId);
      if (!match) return;
      // 193 -- dragging a selected card while others are selected moves the
      // whole selection by the time delta (time-only; courts unchanged; no
      // occupied-cell guard -- 191's framing shows any clash it creates).
      if (selectMode && selectedIds.has(matchId) && selectedIds.size > 1) {
        const [h, mm] = time.split(":").map(Number);
        const target = new Date(match.starts_at);
        target.setHours(h, mm, 0, 0);
        const deltaMins = Math.round((target.getTime() - new Date(match.starts_at).getTime()) / 60_000);
        if (deltaMins !== 0) bulkShiftByIds([...selectedIds], deltaMins);
        return;
      }
      // Can't stack onto another match in this court+slot.
      if (findMatchAt(courtId, time, matchId)) return;
      moveMatch(match, courtId, time);
      return;
    }
  }

  useImperativeHandle(ref, () => ({ onDragEnd: handleDragEnd }));

  // ─── Resize ───────────────────────────────────────────────────

  const resizingRef = useRef<{ matchId: string; startY: number; startMins: number; deltaMins: number } | null>(null);
  const [resizeDelta, setResizeDelta] = useState<{ matchId: string; deltaMins: number } | null>(null);

  function startResize(e: React.MouseEvent, matchId: string, totalMins: number) {
    e.preventDefault();
    e.stopPropagation();
    resizingRef.current = { matchId, startY: e.clientY, startMins: totalMins, deltaMins: 0 };

    function onMove(ev: MouseEvent) {
      if (!resizingRef.current) return;
      const dy = ev.clientY - resizingRef.current.startY;
      const deltaMins = Math.round(dy / PX_PER_MIN);
      resizingRef.current.deltaMins = deltaMins;
      setResizeDelta({ matchId: resizingRef.current.matchId, deltaMins });
    }

    function onUp() {
      if (!resizingRef.current) return;
      const { matchId: mId, startMins, deltaMins: delta } = resizingRef.current;
      const requestedTotal = Math.max(2, startMins + delta);
      const match = matches.find((m) => m.id === mId);
      if (match) {
        // Halves are always equal whole minutes. Round play time (total minus
        // break) to the nearest even integer so it splits cleanly in half.
        const playMins = Math.max(2, Math.round((requestedTotal - match.break_mins) / 2) * 2);
        const half = playMins / 2;
        const newTotal = playMins + match.break_mins;
        // Optimistic
        optimisticUpdateMatch(mId, {
          first_half_mins: half,
          second_half_mins: half,
          ends_at: new Date(new Date(match.starts_at).getTime() + newTotal * 60_000).toISOString(),
        });
        // API
        api.updateMatch(mId, { first_half_mins: half, second_half_mins: half })
          .catch(() => refreshMatches());
      }
      resizingRef.current = null;
      setResizeDelta(null);
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    }

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }

  // ─── Render ───────────────────────────────────────────────────

  // 194 -- the event's primary venue (venues are sorted primary-first).
  // Null-venue courts (fields added via the Courts manager before 194, which
  // never set event_venue_id) fold into it at read time so they group and
  // title correctly without a backfill migration.
  const primaryVenue = event.venues.find((v) => v.is_primary) ?? event.venues[0] ?? null;

  const venueGroups = useMemo(() => {
    const groups: { venueId: string | null; venueName: string; count: number }[] = [];
    const venueMap = new Map<string | null, number>();
    for (const c of displayCourts) {
      const vid = c.event_venue_id ?? primaryVenue?.id ?? null;
      if (venueMap.has(vid)) {
        groups[venueMap.get(vid)!].count++;
      } else {
        const venueName = vid ? (event.venues.find((v) => v.id === vid)?.display_name ?? "Venue") : "";
        venueMap.set(vid, groups.length);
        groups.push({ venueId: vid, venueName, count: 1 });
      }
    }
    return groups;
  }, [displayCourts, event.venues, primaryVenue?.id]);

  const showVenueGrouping = venueGroups.some((g) => g.venueId !== null) && venueGroups.length > 1;
  // 194 -- show the venue title/bar whenever the event has at least one venue
  // (was `> 1`, which hid it for the common single-venue home fixture -- the
  // "Menlo Park" bug). Per-column grouping still wins for genuine multi-venue.
  const showVenueBar = !showVenueGrouping && event.venues.length >= 1;

  // 184: venueGroups (useMemo) moved above this early return so every hook --
  // including the new useImperativeHandle -- runs unconditionally each render.
  if (timeSlots.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center gap-2 py-12">
        <p className="text-muted-foreground">Set event start and end times to see the schedule grid</p>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto" data-region="grid">
      <div className="min-w-fit">
        {canManageMatches && (
          <div className="flex flex-wrap items-center gap-2 border-b bg-muted/30 px-3 py-1.5" data-region="grid-toolbar">
            <button
              type="button"
              data-action="toggle-select-mode"
              onClick={() => (selectMode ? exitSelectMode() : setSelectMode(true))}
              className={`rounded-md border px-2.5 py-1 text-xs font-medium transition ${selectMode ? "border-primary bg-primary/10 text-primary" : "border-input hover:border-primary"}`}
            >
              {selectMode ? "Done" : "Select"}
            </button>
            {selectMode && (
              <>
                <span className="text-xs text-muted-foreground" data-field="selected-count">
                  {selectedIds.size} selected
                </span>
                <button
                  type="button"
                  data-action="move-selected"
                  disabled={selectedIds.size === 0}
                  onClick={() => setShiftMode({ kind: "selection", matchIds: [...selectedIds] })}
                  className="rounded-md border border-input px-2.5 py-1 text-xs font-medium transition enabled:hover:border-primary disabled:opacity-40"
                >
                  Move selected
                </button>
                <button
                  type="button"
                  data-action="clear-selection"
                  disabled={selectedIds.size === 0}
                  onClick={() => setSelectedIds(new Set())}
                  className="rounded-md px-2 py-1 text-xs text-muted-foreground transition enabled:hover:text-foreground disabled:opacity-40"
                >
                  Clear
                </button>
                <span className="hidden text-xs text-muted-foreground/70 md:inline">
                  Tap matches to select; drag any selected match to move the group in time.
                </span>
              </>
            )}
          </div>
        )}
        {showVenueBar && (
          <div
            className="sticky top-0 z-20 flex flex-wrap items-center gap-2 border-b bg-muted/40 px-3 py-2"
            data-region="venue-bar"
          >
            <span className="text-xs font-semibold text-muted-foreground">
              {event.venues.length === 1 ? "Venue:" : "Venues:"}
            </span>
            {event.venues.map((venue) => {
              return (
                <a
                  key={venue.id}
                  href={directionsUrl(venue)}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 rounded-full border border-border bg-background px-2.5 py-0.5 text-xs hover:bg-accent hover:text-accent-foreground"
                  data-item="venue-pill"
                  data-item-id={venue.id}
                >
                  <span className="font-medium">{venue.display_name}</span>
                  {venue.is_primary && (
                    <span className="text-[10px] uppercase tracking-wide text-primary">Primary</span>
                  )}
                  <span aria-hidden className="opacity-60">·</span>
                  <span className="text-[11px] underline-offset-2 hover:underline">Directions</span>
                </a>
              );
            })}
          </div>
        )}
        {showVenueGrouping && (
          <div
            className="sticky top-0 z-20 flex border-b bg-background"
            data-region="venue-group-header"
          >
            <div className="w-14 flex-shrink-0 border-r" />
            {venueGroups.map((group) => (
              <div
                key={group.venueId ?? "__none__"}
                className={`border-r px-2 py-1 text-center text-[11px] font-semibold ${group.venueId ? "bg-amber-50 text-amber-800" : "text-muted-foreground"}`}
                style={{ flex: group.count, minWidth: group.count * 180 }}
              >
                {group.venueName}
              </div>
            ))}
          </div>
        )}
        <div
          className={`sticky z-10 flex border-b bg-background ${showVenueGrouping ? "top-[28px]" : showVenueBar ? "top-[36px]" : "top-0"}`}
        >
          <div className="w-14 flex-shrink-0 border-r px-2 py-2 text-xs font-semibold text-muted-foreground">Time</div>
          {displayCourts.map((court, ci) => {
            const cc = COURT_COLORS[ci % COURT_COLORS.length];
            return (
              <div key={court.id} className={`min-w-[180px] flex-1 border-r px-3 py-2 text-center text-xs font-semibold ${cc.headerBg} ${cc.text}`}>{court.name}</div>
            );
          })}
        </div>

        <div className="relative flex">
          <div className="w-14 flex-shrink-0">
            {timeSlots.map((time) => {
              const mm = Number(time.slice(3));
              const isMajor = mm % MAJOR_LABEL_EVERY_MINS === 0;
              const borderCls = isMajor ? "border-t border-border" : "";
              return (
                <div key={time}
                  className={`${borderCls} border-r border-border/30 px-2 text-right font-mono text-muted-foreground/70`}
                  style={{ height: SLOT_HEIGHT, lineHeight: `${SLOT_HEIGHT}px`, fontSize: 10 }}>
                  {isMajor ? time : ""}
                </div>
              );
            })}
          </div>

          {displayCourts.map((court, ci) => {
            const courtMatches = matchesByCourt.get(court.id) ?? [];
            const cc = COURT_COLORS[ci % COURT_COLORS.length];
            return (
              <div key={court.id} className={`relative min-w-[180px] flex-1 ${cc.columnBg}`}>
                {timeSlots.map((time) => {
                  const match = findMatchAt(court.id, time);
                  const isEmpty = !match;
                  const mm = Number(time.slice(3));
                  const isMajor = mm % MAJOR_LABEL_EVERY_MINS === 0;
                  const borderCls = isMajor ? "border-t border-border/60" : "";
                  const cellClass = `${borderCls} border-r border-border/30 transition-colors ${isEmpty ? "hover:bg-muted/20" : ""}`;
                  return canManageMatches ? (
                    <CellDroppable key={time} courtId={court.id} time={time} slotHeight={SLOT_HEIGHT} className={cellClass} />
                  ) : (
                    <div key={time} className={cellClass} style={{ height: SLOT_HEIGHT }} />
                  );
                })}

                {courtMatches.map((m) => (
                  <MatchCard key={m.id}
                    match={m}
                    teams={matchTeams[m.id] ?? []}
                    result={matchResults?.[m.id]}
                    flash={matchFlashes?.[m.id]}
                    isConflicting={conflictIds.has(m.id)}
                    selectMode={selectMode}
                    isSelected={selectedIds.has(m.id)}
                    onToggleSelect={() => toggleSelected(m.id)}
                    onShiftFromHere={canManageMatches ? (mm) => setShiftMode({ kind: "from", match: mm }) : undefined}
                    teamColorMap={teamColorMap}
                    teamSchoolMap={teamSchoolMap}
                    canManage={canManageMatches}
                    onQuickPairCounterparty={singleCounterparty ? handleQuickPairCounterparty : undefined}
                    counterpartyName={singleCounterparty?.name}
                    setMatches={setMatches}
                    setMatchTeams={setMatchTeams}
                    refreshMatches={refreshMatches}
                    onResizeStart={startResize}
                    resizeDeltaMins={resizeDelta?.matchId === m.id ? resizeDelta.deltaMins : 0}
                    pxPerMin={PX_PER_MIN}
                    gridStartMs={gridStartMs}
                    slotHeight={SLOT_HEIGHT}
                  />
                ))}
              </div>
            );
          })}
        </div>
      </div>

      {/* 193 -- run-late / move-selected bulk shift */}
      {shiftMode !== null && (
        <ShiftMatchesDialog
          eventId={event.id}
          matches={matches}
          mode={shiftMode}
          courtLabel={courtLabel}
          setMatches={setMatches}
          refreshMatches={refreshMatches}
          onClose={() => setShiftMode(null)}
        />
      )}
    </div>
  );
});
